// src/pages/admin/ApprovalRequestsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { db, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, getDocs, doc, deleteDoc } from 'firebase/firestore';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
    Typography, CircularProgress, Tooltip, Snackbar, Alert, Dialog, DialogTitle,
    DialogContent, DialogActions, Button, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

function ApprovalRequestsPage() {
    const [requests, setRequests] = useState([]);
    const [gerentes, setGerentes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [selectedGerenteId, setSelectedGerenteId] = useState('');
    const [duplicateWarning, setDuplicateWarning] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const reqQuery = query(collection(db, "solicitacoes_cadastro"), where("status", "==", "pendente"));
            const reqSnapshot = await getDocs(reqQuery);
            const reqList = reqSnapshot.docs.map(d => ({ ...d.data(), id: d.id }));
            reqList.sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));
            setRequests(reqList);

            const gerentesQuery = query(collection(db, "usuarios"), where("tipoUsuario", "==", "gerente"));
            const gerentesSnapshot = await getDocs(gerentesQuery);
            const gerentesList = gerentesSnapshot.docs.map(d => ({ ...d.data(), id: d.id }));
            gerentesList.sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));
            setGerentes(gerentesList);
        } catch (error) {
            console.error("Erro ao buscar dados:", error);
            setSnackbar({ open: true, message: 'Erro ao buscar dados.', severity: 'error' });
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const openApprovalDialog = async (request) => {
        setSelectedRequest(request);
        setDuplicateWarning(null);
        setIsApprovalDialogOpen(true);

        // Verificação de Duplicidade
        try {
            const usersRef = collection(db, "usuarios");
            let warnings = [];

            if (request.cpf) {
                const qCpf = query(usersRef, where("cpf", "==", request.cpf));
                const snapCpf = await getDocs(qCpf);
                if (!snapCpf.empty) warnings.push(`CPF ${request.cpf} já cadastrado.`);
            }

            if (request.cnpj) {
                const qCnpj = query(usersRef, where("cnpj", "==", request.cnpj));
                const snapCnpj = await getDocs(qCnpj);
                if (!snapCnpj.empty) warnings.push(`CNPJ ${request.cnpj} já cadastrado.`);
            }

            if (warnings.length > 0) {
                setDuplicateWarning(warnings.join(" "));
            }
        } catch (error) {
            console.error("Erro ao verificar duplicidade:", error);
        }
    };

    const closeApprovalDialog = () => {
        setSelectedRequest(null);
        setSelectedGerenteId('');
        setDuplicateWarning(null);
        setIsApprovalDialogOpen(false);
    };

    const handleApprove = async () => {
        if (!selectedRequest) return;

        try {
            const createPartnerFunction = httpsCallable(functions, 'createPartner');

            // Senha gerada com segurança pelo servidor — não enviamos senha do cliente
            const userData = {
                email: selectedRequest.email,
                nome: selectedRequest.nome,
                cpf: selectedRequest.cpf || '',
                cnpj: selectedRequest.cnpj || '',
                rg: selectedRequest.rg || '',
                creci: selectedRequest.creci || '',
                telefone: selectedRequest.telefone || '',
                endereco: selectedRequest.endereco || '',
                tipoUsuario: selectedRequest.tipoUsuario,
                zonaAtuacao: selectedRequest.zonaAtuacao || [],
                status: 'ativo',
                gerenteId: selectedGerenteId || null
            };

            await createPartnerFunction(userData);
            await deleteDoc(doc(db, "solicitacoes_cadastro", selectedRequest.id));

            setSnackbar({ open: true, message: `Parceiro ${selectedRequest.nome} aprovado e criado!`, severity: 'success' });
            closeApprovalDialog();
            fetchData();
        } catch (error) {
            console.error("Erro ao aprovar:", error);
            setSnackbar({ open: true, message: 'Erro ao aprovar a solicitação.', severity: 'error' });
        }
    };

    const handleDeny = async (requestId, requestName) => {
        if (!window.confirm(`Tem certeza que deseja negar o cadastro de ${requestName}?`)) return;
        try {
            await deleteDoc(doc(db, "solicitacoes_cadastro", requestId));
            setSnackbar({ open: true, message: 'Solicitação negada.', severity: 'info' });
            fetchData();
        } catch (error) {
            setSnackbar({ open: true, message: 'Erro ao negar a solicitação.', severity: 'error' });
        }
    };

    const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

    return (
        <>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Nome / Razão Social</TableCell>
                            <TableCell>E-mail</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>CRECI</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={5} align="center"><CircularProgress /></TableCell></TableRow>
                        ) : requests.length === 0 ? (
                            <TableRow><TableCell colSpan={5} align="center">Nenhuma solicitação pendente.</TableCell></TableRow>
                        ) : (
                            requests.map((req) => (
                                <TableRow key={req.id}>
                                    <TableCell>{req.nome}</TableCell>
                                    <TableCell>{req.email}</TableCell>
                                    <TableCell>{req.tipoUsuario}</TableCell>
                                    <TableCell>{req.creci}</TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Aprovar Cadastro">
                                            <IconButton onClick={() => openApprovalDialog(req)} color="success">
                                                <CheckCircleIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Negar Cadastro">
                                            <IconButton onClick={() => handleDeny(req.id, req.nome)} color="error">
                                                <CancelIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={isApprovalDialogOpen} onClose={closeApprovalDialog}>
                <DialogTitle>Aprovar e Vincular Parceiro</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Selecione um gerente para ser o responsável por <strong>{selectedRequest?.nome}</strong>.
                    </Typography>

                    {duplicateWarning && (
                        <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                            <strong>Atenção:</strong> {duplicateWarning}
                        </Alert>
                    )}

                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Gerente Responsável</InputLabel>
                        <Select
                            value={selectedGerenteId}
                            label="Gerente Responsável"
                            onChange={(e) => setSelectedGerenteId(e.target.value)}
                        >
                            <MenuItem value=""><em>-- Nenhum Gerente --</em></MenuItem>
                            {gerentes.map((g) => (
                                <MenuItem key={g.id} value={g.id}>{g.nome}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeApprovalDialog}>Cancelar</Button>
                    <Button onClick={handleApprove} variant="contained">Aprovar Cadastro</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
            </Snackbar>
        </>
    );
}

export default ApprovalRequestsPage;